import React, { useEffect, useState } from 'react';
import { Col, Row, Card } from 'react-bootstrap';
import { HVAPOC, getPOCsByGroup } from 'services/hvaagent.service';
import { hvaImagesandVoices } from 'pages/hva/hvaImagesandVoices';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import Button from 'components/base/Button';
import HVADebtCollectionModal from './DebtCollection/HVADebtCollectionModal';
import HVARealEstateModal from './RealEstate/HVARealEstateModal';
import HVAHealthCareContainer from './HealthCare/HVAHealthCareContainer';
import HVAProductAndOrdersModal from './ProductAndOrders/HVAProductAndOrdersModal';
import HVAContractVerificationModal from './ContractVerification/HVAContractVerificationModal';
import spanish from 'assets/img/flags/spain.png'; // Importar banderas
import english from 'assets/img/flags/england.png';
import greek from 'assets/img/flags/greece.png';
import mexican from 'assets/img/flags/mexico.png';
import german from 'assets/img/flags/germany.png';
import brasilian from 'assets/img/flags/brazil.png';
import italian from 'assets/img/flags/italy.png';
import chilean from 'assets/img/flags/chile.png';
import turkish from 'assets/img/flags/turkey.png';
import colombian from 'assets/img/flags/colombia.png';
import peruvian from 'assets/img/flags/peru.png';

type Language =
  | 'spanish'
  | 'english'
  | 'greek'
  | 'mexican'
  | 'german'
  | 'brasilian'
  | 'italian'
  | 'chilean'
  | 'turkish'
  | 'colombian'
  | 'peruvian';

const HVAPOCServinform = () => {
  const [pocs, setPocs] = useState<HVAPOC[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedPOC, setSelectedPOC] = useState<HVAPOC | null>(null);
  const [showHealthCarePage, setShowHealthCarePage] = useState(false);

  // Aquí cargamos las POCs disponibles
  useEffect(() => {
    const fetchPOCs = async () => {
      try {
        const pocsData = await getPOCsByGroup('servinform');
        setPocs(pocsData);
      } catch (error) {
        console.error('Error fetching POCs:', error);
      }
    };
    fetchPOCs();
  }, []);

  const getImagePath = (bxpname: string): string | undefined => {
    return hvaImagesandVoices[bxpname]?.image;
  };

  const languageToFlag: Record<Language, string> = {
    spanish: spanish,
    english: english,
    greek: greek,
    mexican: mexican,
    german: german,
    brasilian: brasilian,
    italian: italian,
    chilean: chilean,
    turkish: turkish,
    colombian: colombian,
    peruvian: peruvian
  };

  const handleConversationError = (error: Error) => {
    // Handle conversation errors appropriately
    console.error('Conversation error:', error);
    // To show a toast or notification here
  };

  const handleOpenChat = (poc: HVAPOC) => {
    setSelectedPOC(poc);

    // Si es la POC de Healthcare, mostramos la página en lugar del modal
    if (poc.pocform === 5) {
      setShowHealthCarePage(true);
    } else {
      setShowModal(true);
    }
  };

  const handleCloseChat = () => {
    setSelectedPOC(null);
    setShowModal(false);
    setShowHealthCarePage(false);
  };

  // Si showHealthCarePage es true, mostramos la página de healthcare
  if (showHealthCarePage && selectedPOC) {
    return (
      <div className="position-relative">
        <HVAHealthCareContainer
          title={selectedPOC.title}
          agentCode={selectedPOC.agentcode}
          agentName={selectedPOC.name}
          agentImage={getImagePath(selectedPOC.picture)}
          onError={handleConversationError}
          onClose={handleCloseChat}
          firstName="Andrés"
          lastName="González"
          amka="AA12345678"
          phoneNumber="612 345 678"
          firstMessage={selectedPOC.firstmessage}
          prompt={selectedPOC.prompt}
          voice={selectedPOC.voice}
          language={selectedPOC.language}
        />
      </div>
    );
  }

  return (
    <>
      <Row className="gy-3 mb-6 justify-content-between">
        <Col>
          <h2 className="mb-2 text-body-emphasis">Servinform</h2>
          <h5 className="text-body-tertiary fw-semibold">
            Humanized Virtual Agent (HVA) playground.
          </h5>
        </Col>
      </Row>
      <Row className="d-flex p-0">
        {pocs.map(poc => (
          <Col key={poc.id} xl={12} xxl={6} className="pb-3">
            <Card className="h-100 position-relative">
              <Card.Body className="p-6 pt-2">
                <img
                  src={languageToFlag[poc.flag as Language] || spanish}
                  alt="Language"
                  className="position-absolute"
                  style={{ top: '0px', right: '0px' }}
                />
                <Row>
                  <Col>
                    <h3 className="text-body-emphasis pt-5 pb-3">
                      {poc.title}
                    </h3>
                  </Col>
                  <Col
                    sm="auto"
                    xxl="auto"
                    className="d-flex align-items-center justify-content-end pe-6"
                  >
                    {poc.agentcode && (
                      <div className="text-center bg-primary rounded-5 px-3">
                        <Button
                          variant="link"
                          className="p-2 text-decoration-none text-white"
                          onClick={() => handleOpenChat(poc)}
                        >
                          {`Test ${poc.name}`}
                          <FontAwesomeIcon
                            icon={faChevronRight}
                            className="px-1 pe-0"
                          />
                        </Button>
                      </div>
                    )}
                  </Col>
                </Row>
                <Row>
                  <Col
                    sm="auto"
                    xxl="auto"
                    className="d-flex align-items-center justify-content-center p-4 pt-1 pb-0"
                  >
                    {getImagePath(poc.picture) ? (
                      <img
                        src={getImagePath(poc.picture)}
                        alt={`${poc.name}`}
                      />
                    ) : (
                      <p>Image not found</p>
                    )}
                  </Col>
                  <Col className="d-flex flex-column p-4 fs-9 lh-sm pb-0">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: poc.description
                      }}
                    />
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {selectedPOC &&
        (selectedPOC.pocform === 1 ? (
          <HVADebtCollectionModal
            show={showModal}
            onHide={handleCloseChat}
            title={selectedPOC.title}
            agentCode={selectedPOC.agentcode}
            agentName={selectedPOC.name}
            agentImage={getImagePath(selectedPOC.picture)}
            onError={handleConversationError}
            customer="Álvaro Fernández"
            trn="45612398F"
            dateOfBirth="1998-11-13"
            debtamount={327}
            currency="EUR"
            origin="recibos impagados de octubre y noviembre de su préstamo personal"
            IBAN="GR16 0110 1050 0025 4801 2345 678"
            firstMessage={selectedPOC.firstmessage}
            prompt={selectedPOC.prompt}
            voice={selectedPOC.voice}
            language={selectedPOC.language}
            maxDiscount={10}
          />
        ) : selectedPOC.pocform === 2 ? (
          <HVARealEstateModal
            show={showModal}
            onHide={handleCloseChat}
            title={selectedPOC.title}
            agentCode={selectedPOC.agentcode}
            agentName={selectedPOC.name}
            agentImage={getImagePath(selectedPOC.picture)}
            onError={handleConversationError}
            customer="Alberto García"
            dni="51826572B"
            firstMessage={selectedPOC.firstmessage}
            prompt={selectedPOC.prompt}
            voice={selectedPOC.voice}
            language={selectedPOC.language}
          />
        ) : selectedPOC.pocform === 3 ? (
          <HVAContractVerificationModal
            show={showModal}
            onHide={handleCloseChat}
            title={selectedPOC.title}
            agentCode={selectedPOC.agentcode}
            agentName={selectedPOC.name}
            agentImage={getImagePath(selectedPOC.picture)}
            onError={handleConversationError}
            customer="Álvaro Fernández"
            trn="456123987L"
            dateOfBirth={new Date('1998-11-13')}
            service="Electricidad con Pack Iberdrola"
            accountLastDigits="4263"
            firstMessage={selectedPOC.firstmessage}
            prompt={selectedPOC.prompt}
            voice={selectedPOC.voice}
            language={selectedPOC.language}
          />
        ) : selectedPOC.pocform === 4 ? (
          <HVAProductAndOrdersModal
            show={showModal}
            onHide={handleCloseChat}
            title={selectedPOC.title}
            agentCode={selectedPOC.agentcode}
            agentName={selectedPOC.name}
            agentImage={getImagePath(selectedPOC.picture)}
            onError={handleConversationError}
            customerId="CLI-123"
            email="carmona.eduardo@gmail.com"
            name="Eduardo Carmona"
            firstMessage={selectedPOC.firstmessage}
            prompt={selectedPOC.prompt}
            voice={selectedPOC.voice}
            language={selectedPOC.language}
          />
        ) : null)}
    </>
  );
};

export default HVAPOCServinform;
